import { createFileRoute } from '@tanstack/react-router';
import { CaseStudyLayout, type CaseStudyContent } from '../components/CaseStudyLayout.js';
import { BookingMockup } from '../components/visuals/BookingMockup.js';
import { IconPersonal } from '../components/visuals/IndustryIcons.js';
import { usePageTitle } from '../components/usePageTitle.js';

export const Route = createFileRoute('/for-personal-training')({
  component: PersonalTrainingPage,
});

function PersonalTrainingPage() {
  usePageTitle('Bookings for personal trainers');
  return <CaseStudyLayout content={content} />;
}

const content: CaseStudyContent = {
  eyebrow: 'For personal trainers & coaches',
  icon: IconPersonal,
  headline: 'Stop juggling sessions across three apps.',
  headlineEmphasis: 'three apps',
  subhead:
    'Bookaburra runs your 1:1s, group sessions, and bootcamps from one calendar — and lets clients reschedule themselves so you stop fielding texts at 9pm.',
  Visual: BookingMockup,
  business: {
    name: 'PB Movement',
    location: 'Melbourne · 1 trainer · 3 session types',
    summary:
      'Imagine a one-trainer studio in inner Melbourne. The week is a mix of 1:1 sessions (45 min), semi-private pairs (2 clients, 60 min), and a 6am bootcamp (group of 4, 45 min). Six early mornings a week, three evenings. Most of the rescheduling traffic lands after-hours over text.',
  },
  dayTimeline: [
    {
      time: '5:55',
      event:
        '6am bootcamp warm-up. Four clients booked, four spots filled.',
      bookaburraResponse:
        'Capacity is enforced — the picker stopped offering the 6am slot once the fourth client booked. No one is showing up uninvited.',
    },
    {
      time: '7:30',
      event:
        'A new client tries to book the 6:30am Tuesday bootcamp. It’s already full.',
      bookaburraResponse:
        'Slot is not even shown. The picker offers the next bootcamp with a real spot (Thursday 6:30am).',
    },
    {
      time: '11:00',
      event:
        'A 1:1 client texts asking to move tomorrow morning to later in the week.',
      bookaburraResponse:
        'The reminder she received already had a magic-link reschedule — you point her at it and she picks a new slot herself.',
    },
    {
      time: '13:15',
      event:
        'A semi-private pair want to add a third person to their Friday session.',
      bookaburraResponse:
        'The picker won’t offer the third spot — semi-private capacity is set to 2. It nudges them to the bootcamp slot or a separate 1:1.',
    },
    {
      time: '17:20',
      event:
        'A 5:30am client books their next week of sessions from their phone after work.',
      bookaburraResponse:
        '24/7 self-serve. The bookings land on your calendar feed — you find out tomorrow morning, not at 9pm tonight.',
    },
    {
      time: '20:50',
      event:
        'Three different clients click reschedule in their evening reminders.',
      bookaburraResponse:
        'All three move themselves into spots that respect each session type’s capacity. Your phone stays dark.',
    },
  ],
  pains: [
    {
      quote:
        'My phone goes off at 9pm with “any chance we can move tomorrow?” and I’m the bottleneck for every change.',
      explanation:
        'After-hours rescheduling should not depend on me being awake and at my phone.',
    },
    {
      quote:
        'I run 1:1s, semi-privates, and a bootcamp — and I’m holding all three calendars together in my head.',
      explanation:
        'Different session types have different capacity rules. One calendar should handle that, not me.',
    },
    {
      quote:
        'Four people asked for the 6:30am Tuesday slot last week. Only one of them can have it, but I had to tell three of them after the fact.',
      explanation:
        'A slot that’s already full should not be offered to the next client at all.',
    },
  ],
  solutions: [
    {
      title: 'Self-serve magic-link rescheduling (no 9pm texts)',
      body: (
        <>
          Every confirmation and reminder includes a one-tap link to reschedule
          or cancel. Clients pick a new slot themselves — you find out in
          the morning.
        </>
      ),
    },
    {
      title: 'Session-type-aware slot offering',
      body: (
        <>
          A group of four shows four available spots, then fills. A 1:1 takes
          the whole slot. Semi-private holds two. The calendar handles capacity
          per session type without you doing the maths.
        </>
      ),
    },
    {
      title: 'Visible-only-when-available',
      body: (
        <>
          The picker never offers a slot that’s already at capacity. If the
          6:30am Tuesday bootcamp is full, the next client sees the next real
          option — not a slot they’ll get bumped from later.
        </>
      ),
    },
    {
      title: '24-hour and 1-hour reminders',
      body: (
        <>
          A reminder lands the day before and again an hour before, so your
          5:30am clients are up, dressed, and on their way. Reschedule and
          cancel links stay live in those reminders too.
        </>
      ),
    },
  ],
  industrySlug: 'personal-training',
};
